import React, { useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Check, MessageCircle, Mail, ExternalLink, Download, Printer } from "lucide-react";
import { toast } from "sonner";

export default function SharePage() {
  const url = useMemo(() => `${window.location.origin}/apply`, []);
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);

  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQR = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    const src = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const size = 1200;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = "tulsi-jobs-qr.png";
      link.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(src)));
  };

  const printQR = () => window.print();

  const waMsg = encodeURIComponent(
    `Hello! Tulsi Jobs Placement Consultant — kindly fill your details using this link to help us find the right job for you: ${url}`
  );
  const emailSubject = encodeURIComponent("Tulsi Jobs – Candidate Application");
  const emailBody = encodeURIComponent(
    `Hello,\n\nPlease fill out your candidate details using the link below:\n${url}\n\nRegards,\nTulsi Jobs Placement Consultant`
  );

  return (
    <AppShell
      title="Share Public Application Link"
      subtitle="Send this link to candidates over WhatsApp, SMS, or email."
    >
      <div className="max-w-3xl">
        <Card className="tj-card">
          <CardContent className="p-6 md:p-8">
            <Input
              data-testid="public-link-input"
              readOnly
              value={url}
              className="text-sm font-mono"
            />
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <Button
                data-testid="copy-link-btn"
                onClick={copy}
                className="bg-brand-700 hover:bg-brand-800 text-white"
              >
                {copied ? <Check size={16} className="mr-2" /> : <Copy size={16} className="mr-2" />}
                {copied ? "Copied" : "Copy link"}
              </Button>
              <a
                href={`https://wa.me/?text=${waMsg}`}
                target="_blank"
                rel="noreferrer"
                data-testid="share-whatsapp"
              >
                <Button
                  variant="outline"
                  className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                >
                  <MessageCircle size={16} className="mr-2" /> Share on WhatsApp
                </Button>
              </a>
              <a
                href={`mailto:?subject=${emailSubject}&body=${emailBody}`}
                data-testid="share-email"
              >
                <Button variant="outline" className="w-full">
                  <Mail size={16} className="mr-2" /> Share via Email
                </Button>
              </a>
              <a href={url} target="_blank" rel="noreferrer" data-testid="open-link">
                <Button variant="outline" className="w-full">
                  <ExternalLink size={16} className="mr-2" /> Open in new tab
                </Button>
              </a>
            </div>

            <div className="mt-8 rounded-xl bg-brand-50 border border-brand-100 p-5">
              <h3 className="font-heading text-base font-semibold text-brand-900 mb-1">
                What candidates will see
              </h3>
              <p className="text-sm text-ink-700">
                A clean, mobile-friendly multi-step form where they can fill personal info, job
                preferences, education and experience. They can upload their CV + photo, or
                build and download a resume directly inside the form.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="tj-card mt-6 print:shadow-none print:border-0">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-start justify-between gap-4 flex-wrap print:hidden">
              <div>
                <h3 className="font-heading text-lg font-semibold text-ink-900">
                  Scan to Apply — QR code
                </h3>
                <p className="text-sm text-ink-600 mt-1 max-w-md">
                  Print this on posters, flyers, business cards or a laminated
                  sign at your office. Candidates scan it with their phone
                  camera and land straight on the application form.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={downloadQR}
                  data-testid="download-qr-btn"
                  variant="outline"
                  className="border-brand-200 text-brand-700 hover:bg-brand-50"
                >
                  <Download size={16} className="mr-2" /> Download PNG
                </Button>
                <Button
                  onClick={printQR}
                  data-testid="print-qr-btn"
                  variant="outline"
                >
                  <Printer size={16} className="mr-2" /> Print
                </Button>
              </div>
            </div>

            <div
              ref={qrRef}
              className="mt-6 mx-auto max-w-sm bg-white rounded-2xl border border-slate-200 p-6 text-center print:border-0 print:mt-0"
              data-testid="qr-card"
            >
              <div className="font-heading text-lg font-bold text-brand-700">
                TULSI JOBS
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-navy mb-4">
                Placement Consultant
              </div>
              <div className="flex justify-center bg-white p-3 rounded-xl">
                <QRCodeSVG
                  value={url}
                  size={240}
                  level="M"
                  fgColor="#15803d"
                  bgColor="#ffffff"
                  includeMargin={false}
                />
              </div>
              <div className="mt-4 text-sm font-medium text-ink-900">
                Scan to Apply
              </div>
              <div className="text-xs text-ink-600 mt-1">
                Free registration · Get matched with jobs across India
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
